import { useCallback, useState, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Link as LinkIcon,
  Subscript as SubIcon,
  Superscript as SuperIcon,
  Undo,
  Redo,
  IndentIncrease,
  IndentDecrease,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
} from 'lucide-react';

// Custom Link extension with SEO attributes
const CustomLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      rel: {
        default: null,
        parseHTML: (element) => element.getAttribute('rel'),
        renderHTML: (attributes) => {
          if (!attributes.rel) return {};
          return { rel: attributes.rel };
        },
      },
      target: {
        default: null,
        parseHTML: (element) => element.getAttribute('target'),
        renderHTML: (attributes) => {
          if (!attributes.target) return {};
          return { target: attributes.target };
        },
      },
    };
  },
});

export function RichTextEditor({
  content,
  onChange,
  placeholder = 'Start writing...',
  editable = true,
  showHeadings = true,
  className = '',
  isHeading = false,
  headingLevel = null,
}) {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkData, setLinkData] = useState({
    url: '',
    nofollow: false,
    noopener: true,
    noreferrer: true,
    newTab: true,
  });

  // Track whether changes originate from user typing
  const isLocalChange = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: showHeadings ? { levels: [1, 2, 3, 4, 5, 6] } : false,
        bulletList: isHeading ? false : {
          HTMLAttributes: {
            class: 'list-disc ml-4',
          },
        },
        orderedList: isHeading ? false : {
          HTMLAttributes: {
            class: 'list-decimal ml-4',
          },
        },
        listItem: isHeading ? false : undefined,
      }),
      Underline,
      Subscript,
      Superscript,
      CustomLink.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-accent underline',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      isLocalChange.current = true;
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[100px] font-editor',
      },
      transformPastedHTML(html) {
        return html.replace(/style="[^"]*"/gi, (match) => {
          const cleaned = match
            .replace(/font-size\s*:\s*[^;]+;?/gi, '')
            .replace(/font-family\s*:\s*[^;]+;?/gi, '')
            .replace(/color\s*:\s*[^;]+;?/gi, '');
          return cleaned === 'style=""' ? '' : cleaned;
        });
      },
    },
  }, [editable, isHeading, placeholder, showHeadings]);

  // Sync content from parent ONLY when it changes externally (not from typing)
  useEffect(() => {
    if (!editor) return;
    if (isLocalChange.current) {
      isLocalChange.current = false;
      return;
    }
    const currentHTML = editor.getHTML();
    if (content !== currentHTML) {
      editor.commands.setContent(content || '', false);
    }
  }, [content, editor]);

  // Sync editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editable, editor]);

  const handleOpenLinkDialog = useCallback(() => {
    if (!editor) return;
    
    const { href, rel, target } = editor.getAttributes('link');
    
    const relParts = (rel || '').split(' ').filter(Boolean);
    
    setLinkData({
      url: href || '',
      nofollow: relParts.includes('nofollow'),
      noopener: relParts.includes('noopener'),
      noreferrer: relParts.includes('noreferrer'),
      newTab: target === '_blank',
    });
    setLinkDialogOpen(true);
  }, [editor]);

  const handleSetLink = useCallback(() => {
    if (!editor) return;

    if (!linkData.url) {
      editor.chain().focus().unsetLink().run();
      setLinkDialogOpen(false);
      return;
    }

    // Build rel attribute
    const relParts = [];
    if (linkData.nofollow) relParts.push('nofollow');
    if (linkData.noopener) relParts.push('noopener');
    if (linkData.noreferrer) relParts.push('noreferrer');

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({
        href: linkData.url,
        rel: relParts.length > 0 ? relParts.join(' ') : null,
        target: linkData.newTab ? '_blank' : null,
      })
      .run();

    setLinkDialogOpen(false);
  }, [editor, linkData]);

  const handleRemoveLink = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
    setLinkDialogOpen(false);
  }, [editor]);

  // Indent handlers for nested lists (up to 4 levels)
  const handleIndent = useCallback(() => {
    if (!editor) return;
    // Sink list item (increase indent)
    editor.chain().focus().sinkListItem('listItem').run();
  }, [editor]);

  const handleOutdent = useCallback(() => {
    if (!editor) return;
    // Lift list item (decrease indent)
    editor.chain().focus().liftListItem('listItem').run();
  }, [editor]);

  if (!editor) return null;

  const ToolbarButton = ({ onClick, active, disabled, children, title }) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className={`h-8 w-8 p-0 ${active ? 'bg-muted' : ''}`}
      title={title}
      data-testid={`toolbar-${title?.toLowerCase().replace(/\s/g, '-')}`}
    >
      {children}
    </Button>
  );

  const isTransparent = className.includes('bg-transparent');
  const noBorder = className.includes('border-0');
  
  return (
    <div className={`${noBorder ? '' : 'border border-input'} rounded-sm ${isTransparent ? 'bg-transparent' : 'bg-white'} ${className}`} data-testid="rich-text-editor">
      {/* Toolbar */}
      {editable && (
        <div className={`flex flex-wrap items-center gap-0.5 p-1.5 ${noBorder ? '' : 'border-b border-input'} ${isTransparent ? 'bg-slate-200/50' : 'bg-muted/30'}`}>
          {/* Undo/Redo */}
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo"
          >
            <Undo className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo"
          >
            <Redo className="w-4 h-4" />
          </ToolbarButton>

          <div className="w-px h-6 bg-border mx-1" />

          {/* Headings dropdown - only show for non-heading blocks */}
          {showHeadings && !isHeading && (
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-xs font-medium"
                    data-testid="toolbar-heading-dropdown"
                  >
                    Heading
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-40 p-1" align="start">
                  {[1, 2, 3, 4, 5, 6].map((level) => (
                    <Button
                      key={level}
                      variant="ghost"
                      size="sm"
                      onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
                      className={`w-full justify-start ${
                        editor.isActive('heading', { level }) ? 'bg-muted' : ''
                      }`}
                      data-testid={`heading-${level}`}
                    >
                      H{level}
                    </Button>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => editor.chain().focus().setParagraph().run()}
                    className={`w-full justify-start ${
                      editor.isActive('paragraph') ? 'bg-muted' : ''
                    }`}
                    data-testid="paragraph"
                  >
                    Paragraph
                  </Button>
                </PopoverContent>
              </Popover>

              <div className="w-px h-6 bg-border mx-1" />
            </>
          )}

          {/* Text Formatting */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            title="Bold"
          >
            <Bold className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
            title="Italic"
          >
            <Italic className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive('underline')}
            title="Underline"
          >
            <UnderlineIcon className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            active={editor.isActive('strike')}
            title="Strikethrough"
          >
            <Strikethrough className="w-4 h-4" />
          </ToolbarButton>

          <div className="w-px h-6 bg-border mx-1" />

          {/* Subscript/Superscript */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleSubscript().run()}
            active={editor.isActive('subscript')}
            title="Subscript"
          >
            <SubIcon className="w-4 h-4" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleSuperscript().run()}
            active={editor.isActive('superscript')}
            title="Superscript"
          >
            <SuperIcon className="w-4 h-4" />
          </ToolbarButton>

          <div className="w-px h-6 bg-border mx-1" />

          {/* Lists - Hidden for heading blocks */}
          {!isHeading && (
            <>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                active={editor.isActive('bulletList')}
                title="Bullet List"
              >
                <List className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                active={editor.isActive('orderedList')}
                title="Ordered List"
              >
                <ListOrdered className="w-4 h-4" />
              </ToolbarButton>
              
              {/* Indent/Outdent for lists */}
              <ToolbarButton
                onClick={handleOutdent}
                disabled={!editor.can().liftListItem('listItem')}
                title="Decrease Indent"
              >
                <IndentDecrease className="w-4 h-4" />
              </ToolbarButton>
              <ToolbarButton
                onClick={handleIndent}
                disabled={!editor.can().sinkListItem('listItem')}
                title="Increase Indent"
              >
                <IndentIncrease className="w-4 h-4" />
              </ToolbarButton>

              <div className="w-px h-6 bg-border mx-1" />
            </>
          )}

          {/* Link */}
          <ToolbarButton
            onClick={handleOpenLinkDialog}
            active={editor.isActive('link')}
            title="Link"
          >
            <LinkIcon className="w-4 h-4" />
          </ToolbarButton>
        </div>
      )}

      {/* Editor Content */}
      <div className="p-4" style={isHeading && headingLevel ? {
        fontSize: { 1: '30px', 2: '26px', 3: '24px', 4: '22px', 5: '20px', 6: '18px' }[headingLevel],
        fontWeight: headingLevel <= 3 ? 700 : headingLevel <= 4 ? 600 : 500,
        lineHeight: 1.3,
      } : undefined}>
        <EditorContent editor={editor} />
      </div>

      {/* Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">Insert Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="link-url">URL</Label>
              <Input
                id="link-url"
                type="url"
                placeholder="https://example.com"
                value={linkData.url}
                onChange={(e) => setLinkData({ ...linkData, url: e.target.value })}
                data-testid="link-url-input"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-sm text-muted-foreground">SEO Attributes</Label>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="link-newTab"
                  checked={linkData.newTab}
                  onCheckedChange={(checked) =>
                    setLinkData({ ...linkData, newTab: checked })
                  }
                  data-testid="link-newtab-checkbox"
                />
                <Label htmlFor="link-newTab" className="text-sm font-normal">
                  Open in new tab (target="_blank")
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="link-nofollow"
                  checked={linkData.nofollow}
                  onCheckedChange={(checked) =>
                    setLinkData({ ...linkData, nofollow: checked })
                  }
                  data-testid="link-nofollow-checkbox"
                />
                <Label htmlFor="link-nofollow" className="text-sm font-normal">
                  nofollow (tell search engines not to follow)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="link-noopener"
                  checked={linkData.noopener}
                  onCheckedChange={(checked) =>
                    setLinkData({ ...linkData, noopener: checked })
                  }
                  data-testid="link-noopener-checkbox"
                />
                <Label htmlFor="link-noopener" className="text-sm font-normal">
                  noopener (security: prevent opener access)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="link-noreferrer"
                  checked={linkData.noreferrer}
                  onCheckedChange={(checked) =>
                    setLinkData({ ...linkData, noreferrer: checked })
                  }
                  data-testid="link-noreferrer-checkbox"
                />
                <Label htmlFor="link-noreferrer" className="text-sm font-normal">
                  noreferrer (hide referrer information)
                </Label>
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button
                variant="ghost"
                onClick={handleRemoveLink}
                disabled={!editor?.isActive('link')}
                data-testid="remove-link-btn"
              >
                Remove Link
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSetLink} data-testid="apply-link-btn">
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Simplified editor for brief fields (no headings)
export function BriefFieldEditor({
  content,
  onChange,
  placeholder,
  editable = true,
}) {
  return (
    <RichTextEditor
      content={content}
      onChange={onChange}
      placeholder={placeholder}
      editable={editable}
      showHeadings={false}
      className="min-h-[80px]"
    />
  );
}

export default RichTextEditor;
